import React, { useEffect, useRef, useState } from "react";
import { CaretLeft, CaretRight, Palette, Ruler } from "@phosphor-icons/react";
import { Tooltip } from "antd";
import AuthenticatedImage, { AuthenticatedImagePlaceholder } from "../../shared/AuthenticatedImage.tsx";
import { t, getDirection } from "../../../features/store-language/i18n.ts";
import formatDimension from "./formatDimension.ts";

interface PreviewOption {
  variantId: number;
  addressTitle: string;
  colorId: number | null;
  dimensionId: number | null;
  dimensionTitle: string | null;
  fileId: string | null;
}

interface Color {
  backgroundColorId: number;
  backgroundColorTitle: string;
  backgroundColorHexCode: string;
}

interface Props {
  id: string;
  title: string;
  collection: string;
  colors: Color[];
  options: PreviewOption[];
  selectedOption?: PreviewOption;
  onSelect: (option: PreviewOption | undefined) => void;
  onOpen: (element: HTMLElement) => void;
}

export default function CatalogProductCard(props: Props) {
  const cardRef = useRef<HTMLElement>(null);
  function openProduct() {
    if (cardRef.current) props.onOpen(cardRef.current);
  }
  const swatchesRef = useRef<HTMLDivElement>(null);
  const swatchesViewportRef = useRef<HTMLDivElement>(null);
  const [swatchesWidth, setSwatchesWidth] = useState<number>();
  useEffect(function () {
    const viewport = swatchesViewportRef.current;
    if (!viewport) return;
    function updateWidth() {
      if (!viewport) return;
      const available = viewport.clientWidth;
      // Reserve half a swatch at the trailing edge whenever more colors exist.
      const width = props.colors.length * 32 > available
        ? Math.min(available, Math.max(16, Math.floor((available - 16) / 32) * 32 + 16))
        : available;
      setSwatchesWidth(width);
    }
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(viewport);
    return function () { observer.disconnect(); };
  }, [props.colors.length]);
  const drag = useRef({ active: false, moved: false, startX: 0, scrollLeft: 0 });
  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    drag.current = { active: true, moved: false, startX: event.clientX, scrollLeft: event.currentTarget.scrollLeft };
  }
  function moveDrag(event: React.PointerEvent<HTMLDivElement>) {
    if (!drag.current.active) return;
    const distance = event.clientX - drag.current.startX;
    if (!drag.current.moved) {
      if (Math.abs(distance) <= 8) return;
      drag.current.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.classList.add("is-dragging");
    }
    event.currentTarget.scrollLeft = drag.current.scrollLeft - distance;
  }
  function endDrag(event: React.PointerEvent<HTMLDivElement>) {
    drag.current.active = false;
    event.currentTarget.classList.remove("is-dragging");
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }
  useEffect(function () {
    const rail = swatchesRef.current;
    const selected = rail?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!rail || !selected) return;
    const railBounds = rail.getBoundingClientRect();
    const selectedBounds = selected.getBoundingClientRect();
    const padding = 3;
    if (selectedBounds.left < railBounds.left + padding) {
      rail.scrollLeft += selectedBounds.left - railBounds.left - padding;
    } else if (selectedBounds.right > railBounds.right - padding) {
      rail.scrollLeft += selectedBounds.right - railBounds.right + padding;
    }
  }, [props.selectedOption?.colorId, swatchesWidth]);
  const Arrow = getDirection() === "rtl" ? CaretRight : CaretLeft;

  return (
    <article
      ref={cardRef}
      id={props.id}
      className="catalog-gallery-card"
    >
      <div
        className="catalog-gallery-card__image"
        role="link"
        tabIndex={0}
        aria-label={t("مشاهده {{value0}}", { value0: props.title })}
        onClick={openProduct}
        onKeyDown={function (event) {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openProduct();
          }
        }}
      >
        {props.selectedOption?.fileId ? (
          <AuthenticatedImage
            fileId={props.selectedOption.fileId}
            alt={props.title}
            height={320}
            disablePreview={true}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <AuthenticatedImagePlaceholder animated={false} />
        )}
      </div>
      {props.colors.length > 0 && (
        <div
          className="catalog-gallery-card__colors"
          onClick={function (event) { event.stopPropagation(); }}
          onKeyDown={function (event) { event.stopPropagation(); }}
        >
          <button
            type="button"
            className="catalog-gallery-card__count"
            aria-label={t("مشاهده {{value0}}", { value0: props.title })}
            onClick={function (event) {
              event.stopPropagation();
              openProduct();
            }}
          >
            <Arrow
              size={16}
              aria-hidden="true"
            />
            {t("{{count}} رنگ", { count: props.colors.length })}
          </button>
          <div
            ref={swatchesViewportRef}
            className="catalog-gallery-card__swatches-viewport"
          >
            <div
              ref={swatchesRef}
              className="catalog-gallery-card__swatches"
              style={{ width: swatchesWidth }}
              onPointerDown={startDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onClickCapture={function (event) {
                if (drag.current.moved) {
                  event.preventDefault();
                  event.stopPropagation();
                  drag.current.moved = false;
                }
              }}
              onWheel={function (event) {
                if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                  event.currentTarget.scrollLeft += getDirection() === "rtl" ? -event.deltaY : event.deltaY;
                }
              }}
            >
              {props.colors.map(function (color) {
                const options = props.options.filter(function (option) {
                  return option.colorId === color.backgroundColorId && option.fileId;
                });
                const target = options.find(function (option) {
                  return option.dimensionId === props.selectedOption?.dimensionId;
                }) || options[0];
                const selected = props.selectedOption?.colorId === color.backgroundColorId;
                return (
                  <Tooltip
                    key={color.backgroundColorId}
                    title={color.backgroundColorTitle}
                  >
                    <button
                      type="button"
                      className="catalog-gallery-card__swatch"
                      disabled={!target}
                      aria-label={t("نمایش {{value0}} برای {{value1}}", {
                        value0: color.backgroundColorTitle,
                        value1: props.title,
                      })}
                      aria-pressed={selected}
                      onClick={function (event) {
                        event.stopPropagation();
                        props.onSelect(target);
                      }}
                    >
                      <span
                        className="catalog-gallery-card__swatch-dot"
                        style={{ backgroundColor: `#${color.backgroundColorHexCode}` }}
                        aria-hidden="true"
                      />
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className="catalog-gallery-card__details">
        <h3>
          <button
            type="button"
            className="catalog-gallery-card__title"
            title={props.title}
            onClick={openProduct}
          >
            {props.title}
          </button>
        </h3>
        <div className="catalog-gallery-card__meta">
          <span className="catalog-gallery-card__collection">
            <Palette
              weight="duotone"
              aria-hidden="true"
            />
            <span
              className="catalog-gallery-card__collection-label"
              title={props.collection}
            >
              {props.collection}
            </span>
          </span>
          {props.selectedOption?.dimensionTitle && (
            <span className="catalog-gallery-card__dimension">
              <Ruler
                weight="duotone"
                aria-hidden="true"
              />
              <bdi dir="ltr">{formatDimension(props.selectedOption.dimensionTitle)}</bdi>
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
